import type { ProfessionalTypeEnum } from '@gql/graphql';
import {
  Accordion,
  BulletList,
  Button,
  Container,
  ProLandingSectionContent,
  ScrollArowAnimated,
  Section,
  SectionTitle,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import { STEP_CARD_IMAGES } from './assets';
import separationWhite from './assets/separation-white.svg';
import * as Styled from './styled';
import type { JoinProInfoPageProps } from './types';

export * from './types';

const BACKGROUND_COLOR_PER_PRO_TYPE: Record<ProfessionalTypeEnum, 'purple' | 'blue'> = {
  VET: 'blue',
  GROOMER: 'blue',
  EDUCATOR: 'blue',
};

const TITLE_COLOR_PER_PRO_TYPE: Record<ProfessionalTypeEnum, 'purple' | 'blue'> = {
  // VET: 'purple',
  // GROOMER: 'blue',
  VET: 'blue',
  GROOMER: 'purple',
  EDUCATOR: 'purple',
};

export const JoinProInfoPage: FC<JoinProInfoPageProps> = (props) => {
  const { type, proType, header, sections, onButtonClick, moreCards, faqItems = [] } = props;

  const { translate } = useTranslation();
  const { translate: translateResource } = useTranslation({
    keyPrefix: 'joinPro.info',
  });

  const stepCards = useMemo((): any[] => {
    return Object.values(translateResource('steps.cards', { returnObjects: true }));
  }, [translateResource]);

  return (
    <>
      <Styled.TopSection>
        <Styled.TopSeparation src={separationWhite} />
        <Styled.TopContainer>
          <Styled.TopTitle dangerouslySetInnerHTML={{ __html: header.title }} />
          <Styled.TopSubtitle dangerouslySetInnerHTML={{ __html: header.subtitle }} />
          <Styled.TopButton shrink onClick={onButtonClick} color="blue" size="large">
            {translate('common.buttons.discover')}
          </Styled.TopButton>
          <Styled.ScrollArrowWrapper>
            <ScrollArowAnimated />
          </Styled.ScrollArrowWrapper>
        </Styled.TopContainer>
      </Styled.TopSection>
      <Container>
        {sections.map((section, index) => {
          return (
            <div key={index}>
              <ProLandingSectionContent
                title={section.title}
                titleColor={TITLE_COLOR_PER_PRO_TYPE[proType]}
                // subtitle={section.subtitle}
                image={section.image}
                imageOnLeft={index % 2 === 0}
              >
                <BulletList items={section.texts} />
                {/* {section.texts.map((text, textIndex) => (
                  <p key={textIndex}>{text}</p>
                ))} */}
                <Button shrink onClick={onButtonClick} color="blue" size="large">
                  {translate('common.buttons.knowMore')}
                </Button>
              </ProLandingSectionContent>
            </div>
          );
        })}
        <Section centerChildren>
          <SectionTitle color={TITLE_COLOR_PER_PRO_TYPE[proType]}>
            {translateResource('steps.title')}
          </SectionTitle>
          <Styled.StepCardContainer>
            {stepCards.map((card, index) => {
              return (
                <Styled.StepCardWrapper key={index}>
                  <Styled.StepCard>
                    <Styled.StepCardImg src={STEP_CARD_IMAGES[index]} />
                    <Styled.StepCardTitle>{card.title}</Styled.StepCardTitle>
                    <Styled.StepCardText>{card.text}</Styled.StepCardText>
                  </Styled.StepCard>
                </Styled.StepCardWrapper>
              );
            })}
          </Styled.StepCardContainer>
          <Styled.StepButton size="large" color="blue" shrink onClick={onButtonClick}>
            {translate('common.buttons.iRegister')}
          </Styled.StepButton>
        </Section>

        {!!faqItems.length && (
          <Section>
            <Styled.AccordionWrapper>
              <Accordion
                tabs={faqItems.map((faqItem) => ({
                  title: faqItem.title,
                  content: <Styled.AccordionContent>{faqItem.text}</Styled.AccordionContent>,
                }))}
              />
            </Styled.AccordionWrapper>
          </Section>
        )}

        <Section centerChildren>
          <SectionTitle color={TITLE_COLOR_PER_PRO_TYPE[proType]}>
            {translateResource('more.title')}
          </SectionTitle>
          <Styled.MoreCardsContainer>
            {moreCards.map((card, index) => {
              return (
                <Styled.MoreCardWrapper key={index} to={card.link}>
                  <Styled.MoreCard>
                    <Styled.MoreCardBackground src={card.image} />
                    <Styled.MoreCardContentWrapper
                      backgroundColor={BACKGROUND_COLOR_PER_PRO_TYPE[proType]}
                    >
                      <Styled.MoreCardTitle>{card.title}</Styled.MoreCardTitle>
                      <Styled.MoreCardButton shrink color="light" outlined>
                        {translate('common.buttons.knowMore')}
                      </Styled.MoreCardButton>
                    </Styled.MoreCardContentWrapper>
                  </Styled.MoreCard>
                </Styled.MoreCardWrapper>
              );
            })}
          </Styled.MoreCardsContainer>
        </Section>
      </Container>
    </>
  );
};
